import { cssEase, GAME_DURATION, GAME_EASE, toMs } from "@/lib/motion"

/** Side of the square viewBox of the wheel, in SVG units. */
export const WHEEL_BOX = 240

/** Center of the wheel on both axes of the viewBox. */
export const WHEEL_CENTER = WHEEL_BOX / 2

/** Outer radius of the rim. */
export const RIM_RADIUS = 118

/** Radius of the face the slices fill, inside the rim. */
export const FACE_RADIUS = 101

/** Radius of the ring of bulbs on the rim. */
export const BULB_RADIUS = 109.5

/** How many bulbs light the rim. */
export const BULB_COUNT = 16

/** Radius of the pegs between slices that the pointer clicks against. */
export const PEG_RADIUS = 95.5

/** Radius of the hub cap that carries the store logo. */
export const HUB_RADIUS = 27

/** Smallest label font, in viewBox units (about 10 px on a 280 px wheel). */
export const MIN_LABEL_FONT = 8.5

/** How long a spin takes, in milliseconds. */
export const SPIN_MS = toMs(GAME_DURATION.spin)

const SPIN_CURVE = GAME_EASE.spin

/** CSS easing of a spin: a hard flick that brakes to a crawl, so the last pegs click slowly before it stops. */
export const SPIN_EASING = cssEase(SPIN_CURVE)

const LABEL_OUTER = 93
const LABEL_INNER = HUB_RADIUS + 5
const LINE_HEIGHT_EM = 1.12
const HALF_GLYPH_EM = 0.6
const SLICE_PADDING = 1.5
const FONT_STEP = 0.5
const ADVANCE_MARGIN = 1.03
const WIDE_GLYPH_ADVANCE = 1.25
const CABINET_BOLD_LOWER = [
  0.52, 0.56, 0.53, 0.56, 0.54, 0.37, 0.52, 0.55, 0.23, 0.23, 0.53, 0.23, 0.83, 0.55, 0.55, 0.56, 0.56, 0.45, 0.48, 0.4,
  0.55, 0.5, 0.76, 0.51, 0.51, 0.5,
]
const CABINET_BOLD_UPPER = [
  0.67, 0.64, 0.7, 0.69, 0.58, 0.56, 0.72, 0.68, 0.25, 0.58, 0.65, 0.55, 0.83, 0.68, 0.75, 0.62, 0.75, 0.65, 0.62, 0.56,
  0.65, 0.63, 0.95, 0.65, 0.63, 0.58,
]
const CABINET_BOLD_DIGITS = [0.66, 0.33, 0.6, 0.62, 0.63, 0.63, 0.61, 0.51, 0.61, 0.62]
const CABINET_BOLD_SYMBOLS: Record<string, number> = {
  " ": 0.21,
  "%": 0.76,
  $: 0.63,
  "!": 0.24,
  ".": 0.22,
  ",": 0.22,
  ":": 0.22,
  ";": 0.22,
  "-": 0.33,
  "+": 0.42,
  "=": 0.46,
  "/": 0.41,
  "(": 0.32,
  ")": 0.32,
  "&": 0.76,
  "@": 0.81,
  "#": 0.72,
  "*": 0.4,
  "'": 0.22,
  '"': 0.41,
  "…": 0.62,
}

/** A point at `angle` degrees clockwise from the top and `radius` from the center, in viewBox units. */
export function wheelPoint(angle: number, radius: number): { x: number; y: number } {
  const rad = (angle * Math.PI) / 180
  return { x: WHEEL_CENTER + radius * Math.sin(rad), y: WHEEL_CENTER - radius * Math.cos(rad) }
}

function at(angle: number, radius: number): string {
  const { x, y } = wheelPoint(angle, radius)
  return `${x} ${y}`
}

/** SVG path of slice `index` of `count`, clockwise from the top; a lone slice is the whole face. */
export function wheelSlicePath(index: number, count: number, radius = FACE_RADIUS): string {
  if (count <= 1) {
    const left = WHEEL_CENTER - radius
    const right = WHEEL_CENTER + radius
    return `M ${left} ${WHEEL_CENTER} A ${radius} ${radius} 0 1 1 ${right} ${WHEEL_CENTER} A ${radius} ${radius} 0 1 1 ${left} ${WHEEL_CENTER} Z`
  }
  const slice = 360 / count
  const start = index * slice
  const large = slice > 180 ? 1 : 0
  return `M ${WHEEL_CENTER} ${WHEEL_CENTER} L ${at(start, radius)} A ${radius} ${radius} 0 ${large} 1 ${at(start + slice, radius)} Z`
}

/** SVG path of an arc along `radius` from `start` to `end` degrees, clockwise from the top. */
export function wheelArcPath(start: number, end: number, radius: number): string {
  const large = end - start > 180 ? 1 : 0
  return `M ${at(start, radius)} A ${radius} ${radius} 0 ${large} 1 ${at(end, radius)}`
}

function advance(char: string): number {
  const symbol = CABINET_BOLD_SYMBOLS[char]
  if (symbol !== undefined) return symbol
  const code = char.normalize("NFD").charCodeAt(0)
  if (code >= 97 && code <= 122) return CABINET_BOLD_LOWER[code - 97] ?? WIDE_GLYPH_ADVANCE
  if (code >= 65 && code <= 90) return CABINET_BOLD_UPPER[code - 65] ?? WIDE_GLYPH_ADVANCE
  if (code >= 48 && code <= 57) return CABINET_BOLD_DIGITS[code - 48] ?? WIDE_GLYPH_ADVANCE
  return WIDE_GLYPH_ADVANCE
}

/** Width of `text` in em of the bold Cabinet Grotesk the wheel labels use, rounded up so a label measured with it never overflows. */
export function wheelTextWidth(text: string): number {
  let width = 0
  for (const char of text) width += advance(char)
  return width * ADVANCE_MARGIN
}

/** Radial band a label of `lines` lines at `fontSize` may fill in a slice of a `count`-slice wheel, clear of the hub, both sides of the slice and the pegs. */
export function wheelLabelBand(
  lines: number,
  fontSize: number,
  count: number,
): { inner: number; outer: number; length: number } {
  const half = fontSize * HALF_GLYPH_EM + (fontSize * LINE_HEIGHT_EM * Math.max(0, lines - 1)) / 2
  const reach = half + SLICE_PADDING
  const sides = count >= 3 ? reach / Math.tan(Math.PI / count) : 0
  const inner = Math.max(LABEL_INNER, sides)
  const outer = Math.sqrt(Math.max(0, LABEL_OUTER ** 2 - reach ** 2))
  return { inner, outer, length: Math.max(0, outer - inner) }
}

function baseFont(count: number): number {
  if (count <= 4) return 14
  if (count <= 6) return 13
  if (count <= 8) return 12
  return 11
}

function fits(line: string, fontSize: number, room: number): boolean {
  return wheelTextWidth(line) * fontSize <= room
}

function balancedLines(words: readonly string[]): [string, string] | null {
  let best: [string, string] | null = null
  let bestWidth = Infinity
  for (let split = 1; split < words.length; split += 1) {
    const first = words.slice(0, split).join(" ")
    const second = words.slice(split).join(" ")
    const width = Math.max(wheelTextWidth(first), wheelTextWidth(second))
    if (width < bestWidth) {
      best = [first, second]
      bestWidth = width
    }
  }
  return best
}

function ellipsize(text: string, room: number): string {
  if (wheelTextWidth(text) <= room) return text
  const chars = Array.from(text)
  while (chars.length > 1 && wheelTextWidth(`${chars.join("").trimEnd()}…`) > room) chars.pop()
  return `${chars.join("").trimEnd()}…`
}

function cutToFit(words: readonly string[], room: number): string[] {
  let taken = 1
  while (taken < words.length && wheelTextWidth(words.slice(0, taken + 1).join(" ")) <= room) taken += 1
  const first = ellipsize(words.slice(0, taken).join(" "), room)
  const rest = words.slice(taken).join(" ")
  return rest === "" ? [first] : [first, ellipsize(rest, room)]
}

function fitLabel(words: readonly string[], count: number): { lines: string[]; fontSize: number } {
  const text = words.join(" ")
  const two = balancedLines(words)
  for (let font = baseFont(count); font >= MIN_LABEL_FONT; font -= FONT_STEP) {
    if (fits(text, font, wheelLabelBand(1, font, count).length)) return { lines: [text], fontSize: font }
    const room = wheelLabelBand(2, font, count).length
    if (two && two.every((line) => fits(line, font, room))) return { lines: [...two], fontSize: font }
  }
  const room = wheelLabelBand(2, MIN_LABEL_FONT, count).length / MIN_LABEL_FONT
  return { lines: cutToFit(words, room), fontSize: MIN_LABEL_FONT }
}

/** How one slice label is drawn: turned about the center onto the middle of the slice (never upside down) and centered in its band, in one or two lines. */
export interface WheelLabel {
  rotation: number
  x: number
  lines: string[]
  fontSize: number
  lineHeight: number
}

/** Label of slice `index` of `count`: radial, flipped on the left half to stay upright, as large as fits, wrapped to two balanced lines before shrinking and cut with "…" only when even the smallest font does not fit. */
export function wheelLabel(label: string, index: number, count: number): WheelLabel {
  const slices = Math.max(1, count)
  const middle = (index + 0.5) * (360 / slices)
  const angle = (((middle - 90) % 360) + 360) % 360
  const flipped = angle > 90 && angle < 270
  const words = label.trim().split(/\s+/).filter((word) => word !== "")
  const fit = words.length === 0 ? { lines: [], fontSize: baseFont(slices) } : fitLabel(words, slices)
  const band = wheelLabelBand(Math.max(1, fit.lines.length), fit.fontSize, slices)
  const center = (band.inner + band.outer) / 2
  return {
    rotation: flipped ? angle - 180 : angle > 180 ? angle - 360 : angle,
    x: flipped ? WHEEL_CENTER - center : WHEEL_CENTER + center,
    lines: fit.lines,
    fontSize: fit.fontSize,
    lineHeight: fit.fontSize * LINE_HEIGHT_EM,
  }
}

function bezier(a: number, b: number, t: number): number {
  const u = 1 - t
  return 3 * u * u * t * a + 3 * u * t * t * b + t * t * t
}

/** Eased progress (0 to 1) of a spin at time fraction `t`, solving {@link SPIN_EASING} the way browsers do. */
export function spinProgress(t: number): number {
  if (t <= 0) return 0
  if (t >= 1) return 1
  const [x1, y1, x2, y2] = SPIN_CURVE
  let low = 0
  let high = 1
  let guess = t
  for (let step = 0; step < 32; step += 1) {
    const x = bezier(x1, x2, guess)
    if (Math.abs(x - t) < 1e-6) break
    if (x < t) low = guess
    else high = guess
    guess = (low + high) / 2
  }
  return bezier(y1, y2, guess)
}

/** Wheel rotation, in degrees, `elapsed` ms into a spin from `from` to `to`. */
export function spinAngle(from: number, to: number, elapsed: number): number {
  return from + (to - from) * spinProgress(elapsed / SPIN_MS)
}

/** Slice under the top pointer once the wheel has turned `rotation` degrees clockwise. */
export function sliceUnderPointer(rotation: number, count: number): number {
  const slices = Math.max(1, count)
  const angle = ((-rotation % 360) + 360) % 360
  return Math.floor(angle / (360 / slices)) % slices
}

/** How many slice borders have passed the pointer by `rotation` degrees; each change is one click of the pointer. */
export function pegsPassed(rotation: number, count: number): number {
  return Math.floor(rotation / (360 / Math.max(1, count)))
}

/** One confetti piece: where it lands as a share of the side of the wheel, how much further it falls, how far it turns, when it starts, its shape and color slot. */
export interface ConfettiPiece {
  x: number
  y: number
  fall: number
  turn: number
  delay: number
  round: boolean
  color: number
}

const GOLDEN_ANGLE = 137.508

/** A short, even burst of `count` pieces from the hub, the same on every win, colored from `colors` slots. */
export function confettiBurst(count: number, colors: number): ConfettiPiece[] {
  return Array.from({ length: count }, (_, index) => {
    const angle = ((index * GOLDEN_ANGLE) % 360) * (Math.PI / 180)
    const reach = 0.28 + ((index * 7) % 11) / 60
    return {
      x: Math.cos(angle) * reach,
      y: Math.sin(angle) * reach - 0.06,
      fall: 0.08 + (index % 4) * 0.025,
      turn: (index % 2 === 0 ? 1 : -1) * (220 + ((index * 53) % 260)),
      delay: (index % 6) * 35,
      round: index % 3 === 0,
      color: index % Math.max(1, colors),
    }
  })
}

/** Up to two initials of `storeName` (the first letters of its first two words), or "?" when it is blank. */
export function storeInitials(storeName: string): string {
  const letters = storeName
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => Array.from(word)[0]?.toUpperCase() ?? "")
  return letters.join("") || "?"
}
